// CodeHelp.tsx
import React from 'react';
import { Card, Tabs, Tag, Space } from 'antd';
import { LanguageId } from '../core/types';
import { HelpCategory, HelpConfig } from './types';
import { JS_HELP_ITEMS } from './javascript';
import { PY_HELP_ITEMS } from './python';

type ItemsByLanguage = Partial<Record<LanguageId, HelpCategory[]>>;

interface CodeHelpProps {
    language: LanguageId;
    onSelectSnippet: (snippet: string) => void;
    config?: HelpConfig;          // { activeCategories: string[], activeSnippets: Record<cat, string[]> }
    helpGrouped?: boolean;
    projectMode: boolean;
    itemsByLanguage?: ItemsByLanguage; // optionnel: injecter d'autres listes (ex: Python)
}

const CodeHelp: React.FC<CodeHelpProps> = ({
    language,
    onSelectSnippet,
    config,
    helpGrouped = false,
    projectMode = false,
    itemsByLanguage,
}) => {
    // Items par langage (fallback sur DEFAULT_HELP_ITEMS pour JS)
    const ITEMS: HelpCategory[] =
        itemsByLanguage?.[language] ??
        (language === 'javascript' ? JS_HELP_ITEMS : PY_HELP_ITEMS);

    // Filtrage selon config (ou tout si mode projet)
    const categoriesToDisplay = projectMode
        ? ITEMS
        : config?.activeCategories?.length
            ? ITEMS.filter((cat) => config.activeCategories.includes(cat.name))
            : [];

    const filteredCategories = projectMode
        ? ITEMS
        : categoriesToDisplay.map((cat) => {
            const snippetIds = config?.activeSnippets?.[cat.name] || [];
            return { ...cat, snippets: cat.snippets.filter((s) => snippetIds.includes(s.id)) };
        });

    if (helpGrouped) {
        const items = filteredCategories.map((cat) => ({
            key: cat.name,
            label: <span style={{ color: cat.color }}>{cat.name}</span>,
            children: (
                <div style={{ padding: 8 }}>
                    <CategorySnippets
                        snippets={cat.snippets}
                        color={cat.color}
                        onSelectSnippet={onSelectSnippet}
                    />
                </div>
            ),
        }));

        return (
            <Card title="Aide" size="small" style={{ marginTop: 16 }}>
                <Tabs tabPosition="left" items={items} style={{ overflowY: 'auto' }} />
            </Card>
        );
    }

    // Vue non groupée: tout en vrac
    const allSnippets = filteredCategories.flatMap((cat) =>
        cat.snippets.map((s) => ({ ...s, color: cat.color }))
    );

    return (
        <Card title="Aide" size="small" style={{ marginTop: 16 }}>
            <Space wrap>
                {allSnippets.map((snip) => (
                    <Tag
                        key={snip.id}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: (snip as any).color,
                            borderColor: (snip as any).color,
                            color: '#fff',
                        }}
                        onClick={() => onSelectSnippet(snip.code)}
                    >
                        {snip.label}
                    </Tag>
                ))}
            </Space>
        </Card>
    );
};

function CategorySnippets({
    snippets,
    color,
    onSelectSnippet,
}: {
    snippets: HelpCategory['snippets'];
    color: string;
    onSelectSnippet: (code: string) => void;
}) {
    return (
        <Space wrap>
            {snippets.map((snip) => (
                <Tag
                    key={snip.id}
                    onClick={() => onSelectSnippet(snip.code)}
                    style={{ cursor: 'pointer', backgroundColor: color, borderColor: color, color: '#fff' }}
                >
                    {snip.label}
                </Tag>
            ))}
        </Space>
    );
}

export default CodeHelp;
